<template>
	<div id="background" :style="cssBack" ref="root">
		<div id="task" :style="cssTask">

			<div id="infoArea" ref="infoArea">
				<div id="infoBox" :style="infoBoxScaling">

					<!-- Introduction -->
					<div id="infoIntroduction">
						<!-- <h3>{{ taskText('title') }}</h3> -->
						<div v-if="isRunning || !hasExplanation">
							<h4 class="card-text">{{ taskText('introduction_heading') }}</h4>
							<p class="card-text">{{ taskText('introduction_description') }}</p>
						</div>
						<div v-else>
							<h4 class="card-text">{{ taskText('explanation_heading') }}</h4>
							<p class="card-text">{{ taskText('explanation_description') }}</p>
						</div>
						<!-- <h5 class="card-title">Card</h5> -->
						<!-- <h6 class="card-subtitle my-2 text-muted">Subtitle</h6> -->
						<!-- <p class="card-text">Tempor velit commodo.</p> -->
					</div>

					<!-- Instruction -->
					<div id="infoInstruction">
						<!-- During runtime -->
						<div v-if="isRunning">
							<p class="card-text">
								{{ taskText('instruction_default') }}
							</p>
							<p v-if="taskType == 'arrow'" class="card-text text-end">
								<b class="btn btn-warning text-dark progress-bar-striped progress-bar-animated">
									{{ $t('arrows_count', { amount: arrowCount, max: maxArrowCount }) }}
								</b>
							</p>
							<p v-if="taskType == 'carddrag' && !allowVerify" class="card-text text-end">
								<b class="btn btn-warning text-dark progress-bar-striped progress-bar-animated">
									{{ $t('cards_count', { amount: cardCount, max: maxCardCount }) }}
								</b>
							</p>
							<!-- Verify button -->
							<p v-if="allowVerify && taskType == 'carddrag'" class="card-text text-end">
								<b class="btn progress-bar-striped progress-bar-animated"
									:class="{ 'btn-primary': allowVerify, 'btn-secondary': !allowVerify }" @click="onVerify"
									:disabled="!allowVerify">
									{{ $t('quiz_button_verify') }}
								</b>
							</p>
						</div>
						<!-- On completion -->
						<div v-else>
							<p class="card-text" v-if="rightAnswer">
								{{ $t('task_completed_1') }}
							</p>
							<!-- <p class="card-text" v-else> -->
								<!-- {{ taskText('instruction_wrong_answer') }} -->
							<!-- </p> -->
							<!-- Continue button -->
							<div v-if="!isRunning" style="display: flex; justify-content: flex-end; margin-top: -1em;">
								<router-link class="btn btn-success progress-bar-striped progress-bar-animated"
									:to="{ name: 'Module', params: { moduleName, fromTaskName: taskName } }"
									@click.native="next">
									{{ $t('continue') }}
								</router-link>
							</div>
						</div>
					</div>

					<!-- Cards -->
					<div v-if="taskType == 'carddrag'" id="infoCards">
						<div
							v-for="(card,i) in cards"
							v-if="card.activeIf <= myPerformance"
							:key="i"
							class="d-flex flex-wrap p-2"
							:class="[cardWidth]">
							<div
								class="w-100 h-100"
								@pointerdown="onCardDown($event, card.id)"
								@pointerup="onCardUp($event, card.id)"
								@dragenter.prevent
								@dragover.prevent
								@drop="onCardDrop($event, card.id)">
								<Card
									:type="cardType"
									:ref="card.id"
									:cardId="card.id"
									:cardValue="card.value"
									:image="card.image"
									:cardHeight="cardHeight"
									:hasFrontText="taskId == 'm1_t1a'"
									@readMore="onSlotReadMore"
								/>
							</div>
						</div>
					</div>

					<!-- Arrow helper -->
					<div v-if="taskType == 'arrow'" id="infoArrows">
						<div v-for="(hintGroup, cardValue) in arrowHintCount" :key="cardValue" class="arrow-hint form-check">
							<input class="form-check-input" type="checkbox" value="" :checked="hintGroup.remaining < hintGroup.max" :disabled="true" :class="{
								'checkbox-correct': hintGroup.remaining == 0
							}">
							<div v-if="hintGroup.max > 1 && hintGroup.remaining > 0 && !hintGroup.secret" class="arrow-hint-count-wrapper">
								<span class="arrow-hint-count">
									<b>{{ hintGroup.max - hintGroup.remaining }}</b>/<b>{{ hintGroup.max }}</b>
								</span>
							</div>
							<label class="form-check-label arrow-hint-label" :style="{
								'letter-spacing': $t(hintGroup.card).length < 30 ? 'normal' : '-1px'
							}">
								<span v-if="!hintGroup.secret">
									{{ $t(hintGroup.card) }}
								</span>
								<span v-else style="opacity: 0.2;">
									{{ $t(hintGroup.card).replace(/[\wÅÄÖåäö]/g, "&ndash;") }}
								</span>
							</label>
						</div>
					</div>

					<!-- Custom number input area -->
					<div v-if="taskType == 'input'" id="infoCards">
						<m3t2a v-if="taskId == 'm3_t2a'" ref="m3t2a" @complete="onComplete" />
						<m3t2b v-if="taskId == 'm3_t2b'" ref="m3t2b" @complete="onComplete" />
						<m3t3 v-if="taskId == 'm3_t3'" ref="m3t3" @complete="onComplete" />
						<m3t4a v-if="taskId == 'm3_t4a'" ref="m3t4a" @complete="onComplete" />
						<m3t4b v-if="taskId == 'm3_t4b'" ref="m3t4b" @complete="onComplete" />
					</div>

				</div>
			</div>


			<div id="dropArea" style="border: 0px dashed red;">

				<div
					v-for="group in groups"
					:key="group.id">
					<Group
						ref="groups"
						:groupId="group.id"
						:label="group.label"
						:slots="group.slots"
						:pos="group.pos"
						:anchor="group.anchor"
						:boxWidth="group.width"
						:boxHeight="group.height"
						:image="group.image"
						:sliderImages="group.sliderImages"
						:color="group.color"
						:border="group.border"
						:graph="group.graph"
					/>
				</div>

				<div
					v-for="slider in sliders"
					:key="slider.id">
					<Slider
						ref="sliders"
						:sliderId="slider.id"
						:label="slider.label"
						:values="slider.values"
						:subvalues="slider.subvalues"
						:steps="slider.steps"
						:slots="slider.slots"
						:pos="slider.pos"
						:boxWidth="slider.width"
						:boxHeight="slider.height"
						@onChange="onSliderChange"
					/>
				</div>

				<div
					v-for="(slot) in slots"
					:key="slot.id"
					@dragenter.prevent
					@dragover.prevent>
					<CardSlot
						:type="cardType"
						ref="slots"
						:slotId="slot.id"
						:cardValue="slot.cardValue"
						:cardAlts="slot.cardAlts"
						:x="slot.pos.x"
						:y="slot.pos.y"
						:anchor="slot.anchor"
						:slotWidth="slot.width"
						:slotHeight="slot.height"
						:image="slot.image"
						:bgImage="slot.bgimage"
						:arrow="slot.arrow"
						:locked="slot.locked"
						:label="slot.label"
						:hidden="slot.hidden"
						:emptyColor="slot.emptyColor"
						:lockColor="slot.lockColor"
						:allowInput="isRunning"
						:allowArrows="taskType == 'arrow' && (slot.arrows && slot.arrows.length > 0)"
						:allowDrop="draggedCardId || draggedSlotId"
						:arrowCount="slot.arrows ? slot.arrows.length : null"
						:held="heldSlotId == slot.id"
						:hover="closestSlotId == slot.id"
						:selected="closestSlotId && draggedSlotId && (draggedSlotId == slot.id || closestSlotId == slot.id)"
						:incorrect="slot.incorrect"
						:zIndex="(taskType == 'carddrag' || slot.label) ? 1 : 0"
						@labelEnter="onSlotLabelEnter(slot.id)"
						@labelLeave="onSlotLabelLeave(slot.id)"
						@readMore="onSlotReadMore"
						:labelHover="recentArrow && recentArrow.id == slot.arrow"
					/>
				</div>

				<div
					v-for="(auto) in autoslots"
					:key="auto.id">
					<AutoSlot
						:type="cardType"
						ref="autoslots"
						:autoId="auto.id"
						:slotId="auto.slot"
						:x="auto.pos.x"
						:y="auto.pos.y"
						:anchor="auto.anchor"
						:slotWidth="auto.width"
						:slotHeight="auto.height"
						:image="auto.image"
						:remap="auto.remap"
						:text="auto.text"
						:format="auto.format"
						:big="auto.big"
						:color="auto.color"
						:isButton="auto.isButton"
						:pgc="auto.pgc"
						:mapping="mapping"
						@click="onAutoSlotClick"
					/>
				</div>

				<canvas width="960" height="800" id="canvas" style="border: 0px dashed yellow;"></canvas>
			</div>

			<DragSlot
				:type="cardType"
				:cardValue="draggedCardValue"
				:x="pointer.x"
				:y="pointer.y - top"
				:slotWidth="20"
				:zIndex="10"
			/>
		</div>

		<ModalCardInfo ref="modalCardInfo" />

		<!-- Loading icon -->
		<div v-if="!taskType" class="loading-icon">
			<div class="spinner-border text-warning" style="width: 3rem; height: 3rem;" role="status"></div>
		</div>

		<!-- Image preloader -->
		<div
			v-for="image in backgroundPreloadList" :key="image"
			style="position: absolute; left: -10000px; width: 0px; height: 0px;"
			:style="{ backgroundImage: 'url(' + image + ')' }"
		></div>
	</div>
</template>



<script>
import Card from '@/components/Card';
import Group from '@/components/Group';
import Slider from '@/components/Slider';
import CardSlot from '@/components/CardSlot';
import DragSlot from '@/components/DragSlot';
import AutoSlot from '@/components/AutoSlot';
import Timer from '@/components/Timer';
import Graphics from '@/components/Graphics';
import ModalCardInfo from '../components/modals/ModalCardInfo.vue';
import m3t2a from '../components/taskbox/m3t2a.vue';
import m3t2b from '../components/taskbox/m3t2b.vue';
import m3t3 from '../components/taskbox/m3t3.vue';
import m3t4a from '../components/taskbox/m3t4a.vue';
import m3t4b from '../components/taskbox/m3t4b.vue';

export default {
	props: [ "moduleName", "taskName" ],
	components: { Card, Group, Slider, CardSlot, DragSlot, AutoSlot, ModalCardInfo, m3t2a, m3t2b, m3t3, m3t4a, m3t4b },
	data() {
		return {
			sessionLoaded: !!this.$tracker.session,
			taskLoaded: false,
			delayedResizeTimeout: null,

			drag: false,
			taskId: this.routeToId(this.moduleName, this.taskName),
			taskType: "",
			cardType: "",
			cardWidth: "",
			cardHeight: "",
			cards: [],
			groups: [],
			sliders: [],
			slots: [],
			autoslots: [],
			mapping: {},
			isPlaced: {},
			top: 0,

			timer: null,
			graphics: null,
			prevDrawTime: 0,

			isRunning: true,
			allowVerify: false,
			rightAnswer: false,
			draggedCardId: null,
			draggedSlotId: null,
			heldCardId: null,
			heldSlotId: null,
			myPerformance: 50,

			arrows: [],
			arrowHints: [],
			arrowHintCount: {},
			recentArrow: null,
			autoHideLabel: null,
			incorrectCount: 0,

			rawPointer: { x: 0, y: 0 },
			pointer: { x: 0, y: 0 },
			mouse: { x: 0, y: 0 },
			rawMouse: { x: 0, y: 0 },
			dragStart: { x: 0, y: 0 },
			// dragMouse: { x:0, y:0 },
			rawDragMouse: { x: 0, y: 0 },

			background: "",
			backgroundEdges: "",
			backgroundPreloadList: [],
			cssBack: {},
			cssTask: {},
			infoBoxScaling: {},

			average_mistakes: null,
			median_mistakes: null,
			average_time: null,
			median_time: null,
		};
	},
	mounted() {
		window.addEventListener('resize', this.onResize);
		window.addEventListener('pointerup', this.onPointerUp);
		window.addEventListener('pointerdown', this.onPointerDown);
		window.addEventListener('pointermove', this.onPointerMove);

		this.graphics = new Graphics(document.getElementById("canvas"));
		this.timer = new Timer(this.update.bind(this));

		// Fetch task data
		this.fetch(`/api/get_task/${this.moduleName}/${this.taskName}`, data => {
			if (data) {
				this.data = data;

				this.taskLoaded = true;
				if (this.sessionLoaded) {
					this.onTaskLoad();
				}
			}
			else {
				console.error("Error in Task fetch:", this.fetchError);
				this.$router.replace({ name: 'Home' });
			}
		});
	},
	beforeDestroy() {
		if (this.timer) {
			this.timer.clear();
		}
		window.removeEventListener('resize', this.onResize);
		window.removeEventListener('pointerup', this.onPointerUp);
		window.removeEventListener('pointerdown', this.onPointerDown);
		window.removeEventListener('pointermove', this.onPointerMove);
		// this.$el.removeEventListener('pointermove', this.onPointerMove);

		clearTimeout(this.delayedResizeTimeout);
	},
	methods: {
		onSessionLoad() {
			console.log("onSessionLoad");
			// Temporary block for pilot
			// if (!this.$tracker.debug && (this.moduleName == 'module-3' || (this.moduleName == 'module-2' && ['task-4a', 'task-4b', 'task-5', 'task-6a', 'task-6b', 'quiz-2'].includes(this.taskName)))) {
			// 	this.$router.replace(`/${this.moduleName}`);
			// 	return;
			// }

			this.sessionLoaded = true;
			if (this.taskLoaded) {
				this.onTaskLoad();
			}
		},

		onTaskLoad() {
			this.$tracker.onTaskStart(this.taskId);
			console.log("Loading task... Difficulty:", this.$tracker.performance);

			// Successfully loaded task data
			const data = this.data;

			this.taskType = data.task_type || "carddrag";
			this.cardType = data.card_type || "reservoir";
			this.cardWidth = data.card_width;
			this.cardHeight = data.card_height;
			// this.cards = data.cards || [];
			this.groups = data.groups || [];
			this.sliders = data.sliders || [];
			this.slots = data.slots || [];
			this.autoslots = data.autoslots || [];
			this.arrows = data.arrows || [];
			this.mapping = data.mapping || {};
			this.incorrectCount = 0;

			this.average_mistakes = data.average_mistakes;
			this.median_mistakes = data.median_mistakes;
			this.average_time = data.average_time;
			this.median_time = data.median_time;

			this.$log.onTaskStart(this.taskId, this.taskType, this.cardType, this.$tracker.performance);

			this.arrowHints = [];
			this.arrowHintCount = {};
			for (const arrow of this.arrows) {
				const foundFromStart = (!arrow.active && arrow.active_min <= this.$tracker.performance && arrow.active_max >= this.$tracker.performance);
				if (foundFromStart) {
					arrow.active = true;
				}

				if (arrow.card) {
					let label = this.slots.find(slot => slot.id == arrow.card);
					if (label) {
						this.arrowHints.push({
							id: arrow.id,
							// from: arrow.from,
							// to: arrow.to,
							card: `card_${label.cardValue}_title`,
							cardValue: label.cardValue,
							// slow: arrow.slow,
							// active: arrow.active,
							// locked: arrow.locked,
							found: foundFromStart,
						});

						if (this.arrowHintCount[label.cardValue]) {
							this.arrowHintCount[label.cardValue].remaining += 1;
							this.arrowHintCount[label.cardValue].max += 1;
						}
						else {
							this.arrowHintCount[label.cardValue] = {
								cardValue: label.cardValue,
								card: `card_${label.cardValue}_title`,
								remaining: 1,
								max: 1,
								secret: this.$tracker.isHard(),
							};
						}

						if (foundFromStart) {
							this.arrowHintCount[label.cardValue].remaining -= 1;
							this.arrowHintCount[label.cardValue].secret = false;

							label.locked = true;
							this.$log.onArrowReveal(arrow.id, label.cardValue);
						}
					}
				}
			}
			this.revealRandomHintArrow();

			// if (this.arrowHints.length > 0) {
			// 	this.arrowHints.sort((a, b) => {
			// 		if (this.$t(a.card) < this.$t(b.card)) return -1;
			// 		if (this.$t(a.card) > this.$t(b.card)) return 1;
			// 		return 0;
			// 	});

			// 	let halfList = this.arrowHints.splice(Math.ceil(this.arrowHints.length/2));
			// 	this.arrowHints = [this.arrowHints, halfList]
			// 		.reduce((r, a) => (a.forEach((a, i) => (r[i] = r[i] || []).push(a)), r), [])
			// 		.reduce((a, b) => a.concat(b));
			// }

			this.cards = [];
			for (let cardData of data.cards) {
				this.cards.push({
					id: cardData.id,
					value: cardData.value,
					fake: cardData.fake,
					image: cardData.image,
					activeIf: cardData.active_if,
					placeIf: cardData.place_if,
				});
			}
			// if (this.$tracker.isEasy()) {
			// 	// console.log("REMOVE FAKE CARDS");
			// 	this.cards = this.cards.filter(card => !card.fake);
			// }
			// console.log(this.$tracker.behavior, this.$tracker.performance, this.$tracker.performanceValue);
			// console.log(this.cards.map(card => card.difficulty));
			// this.cards = this.cards.filter(card => card.difficulty <= this.$tracker.performance);

			this.shuffle(this.cards);

			for (let slot of this.slots) {
				// slot.pos.x = 0.1 + 0.8 * slot.pos.x;
				// slot.pos.y = 0.1 + 0.8 * slot.pos.y;
				slot.arrows = null;
				slot.incorrect = false;
				if (this.taskType == "arrow" && !slot.label) {
					slot.arrows = this.arrows.filter(arrow => {
						return (arrow.to == slot.id || arrow.from == slot.id)
							&& !arrow.active
							&& !arrow.locked;
							// && !arrow.slow;
					}).map(arrow => arrow.id);
				}
			}

			// for (let auto of this.autoslots) {
				// auto.pos.x = 0.1 + 0.8 * auto.pos.x;
				// auto.pos.y = 0.1 + 0.8 * auto.pos.y;
			// }

			for (let card of this.cards) {
				this.$set(this.isPlaced, card.id, false);
			}
			if (data.background) {
				if (Array.isArray(data.background)) {
					this.background = data.background.map(src => {
						return require(`@/assets/backgrounds/${src}`);
					});
					this.backgroundEdges = data.background_edges.map(src => {
						return require(`@/assets/backgrounds/${src}`);
					});

					this.cssBack.backgroundImage = `url(${this.backgroundEdges[0]})`;
					this.cssTask.backgroundImage = `url(${this.background[0]})`;

					this.backgroundPreloadList = this.background.concat(this.backgroundEdges);
				}
				else {
					this.background = require(`@/assets/backgrounds/${data.background}`);
					this.backgroundEdges = require(`@/assets/backgrounds/${data.background_edges}`);

					this.cssBack.backgroundImage = `url(${this.backgroundEdges})`;
					this.cssTask.backgroundImage = `url(${this.background})`;
				}
			}

			this.onResize();
			this.delayedResizeTimeout = setTimeout(this.onResize, 500);
			this.myPerformance = this.$tracker.performance;
		},

		taskText(key) {
			return this.getText(this.taskId, key);
		},

		getCard(cardId) {
			if (this.$refs[cardId]) {
				return this.$refs[cardId][0];
			}
		},
		getSlot(slotId) {
			for (var i = this.$refs.slots.length - 1; i >= 0; i--) {
				const slot = this.$refs.slots[i];
				if (slot.slotId == slotId) {
					return slot;
				}
			}
		},
		getAutoslot(autoId) {
			for (var i = this.$refs.autoslots.length - 1; i >= 0; i--) {
				const auto = this.$refs.autoslots[i];
				if (auto.autoId == autoId) {
					return auto;
				}
			}
		},
		getGroup(groupId) {
			for (var i = this.$refs.groups.length - 1; i >= 0; i--) {
				const group = this.$refs.groups[i];
				if (group.groupId == groupId) {
					return group;
				}
			}
		},
		getSlider(sliderId) {
			for (var i = this.$refs.sliders.length - 1; i >= 0; i--) {
				const slider = this.$refs.sliders[i];
				if (slider.sliderId == sliderId) {
					return slider;
				}
			}
		},
		getArrow(slotId1, slotId2) {
			return this.arrows.find(arrow => {
				return (arrow.from == slotId1 && arrow.to == slotId2);
			});
		},

		onCardDrag(event, cardId) { // event, cardId
			// console.log("onCardDrag", cardId);
			this.setMouseFromEvent(event);
			this.$log.onCardDrag(cardId, event.x, event.y, this.rawDragMouse.x, this.rawDragMouse.y);
		},
		onCardDragStart(event, cardId) {
			// this.clone = event.target.closest('.card').cloneNode(true);
			// console.log(event.target.clientWidth, event.target.clientHeight);
			// this.clone.style.width = this.clone.getBoundingClientRect().width;
			// this.clone.style.height = this.clone.getBoundingClientRect().height;
			// this.clone.style.left = 0;
			// this.clone.style.top = 0;
			// this.clone.style.position = 'fixed';
			// document.getElementById('copytest').appendChild(this.clone);

			// console.log("onCardDragStart", cardId);
			// event.dataTransfer.effectAllowed = "move";
			// 

			const card = this.getCard(cardId);
			if (card && card.show) {
				this.draggedCardId = cardId;
				card.dragged = true;
				this.$log.onCardDragStart(cardId, event.x, event.y, this.rawMouse.x, this.rawMouse.y);
			}
		},
		onCardDragEnd(event, cardId) {
			// console.log("onCardDragEnd", cardId);
			const card = this.getCard(cardId);
			this.draggedCardId = null;
			if (card) {
				card.dragged = false;
				if (card.show) {
					card.playBounce();
				}
				this.$log.onCardDragEnd(cardId, event.x, event.y, this.rawDragMouse.x, this.rawDragMouse.y);
			}
		},
		onCardDrop(event, cardId) {
			// Dragging a card back to a specific left slot
			// console.log("onCardDrop", cardId);
		},
		onCardDown(event, cardId) {
			let { x, y } = this.graphics.setMouse(this.rawMouse, event.x, event.y);
			this.$set(this.rawMouse, 'x', x);
			this.$set(this.rawMouse, 'y', y);
			this.mouse.x = this.rawMouse.x;
			this.mouse.y = this.rawMouse.y;
			this.rawPointer.x = event.x;
			this.rawPointer.y = event.y;
			this.pointer.x = event.x;
			this.pointer.y = event.y;

			this.heldCardId = cardId;
			this.dragStart.x = event.x;
			this.dragStart.y = event.y;

			// this.onCardDragStart(event, cardId);
		},
		onCardUp(event, cardId) {
			// if (this.taskType == 'arrow') {
			this.onCardDrop(event, cardId);
			// }
		},

		onSlotDrag(event) { // slotId
			this.setMouseFromEvent(event);
			if (this.taskType == "carddrag") {
				this.$log.onSlotDrag(this.draggedSlotId, event.x, event.y, this.rawDragMouse.x, this.rawDragMouse.y);
			}
			else if (this.taskType == "arrow") {
				this.$log.onArrowDrag(this.draggedSlotId, event.x, event.y, this.rawDragMouse.x, this.rawDragMouse.y);
			}
		},
		onSlotDragStart(event, slotId) {
			// this.clone = event.target.closest('.wrapper').cloneNode(true);
			// this.clone.style.position = 'fixed';
			// this.clone.style.height = this.clone.clientHeight;
			// this.clone.style.width = this.clone.clientWidth;
			// document.getElementById('copytest').appendChild(this.clone);

			this.slots.find(slot => slotId == slot.id).incorrect = false;
			this.updateIncorrectCount();

			if (this.isRunning) {
				let slot = this.getSlot(slotId);

				if (this.taskType == "carddrag") {
					if (!slot.empty && !slot.locked) {
						this.draggedSlotId = slotId;
						// event.dataTransfer.setDragImage(slot.$refs.card, slot.width/2, slot.height/2);
						// event.dataTransfer.effectAllowed = "move";
						slot.dragged = true;
						this.$log.onSlotDragStart(slotId, event.x, event.y, this.rawDragMouse.x, this.rawDragMouse.y);
						return true;
					}
				}

				else if (this.taskType == "arrow") {
					if (slot && !slot.empty && slot.arrowCount > 0) {
						this.draggedSlotId = slotId;
						this.recentArrow = null;
						// this.dragStart.x = slot.x * this.graphics.width;
						// this.dragStart.y = slot.y * this.graphics.height;
						// this.dragMouse.x = this.dragStart.x;
						// this.dragMouse.y = this.dragStart.y;
						// event.dataTransfer.setDragImage(slot.$refs.card, -10000, -10000);
						this.$log.onArrowDragStart(slotId, event.x, event.y, this.dragStart.x, this.dragStart.y);
						return true;
					}
				}
			}
			event.preventDefault();
			return false;
		},
		onSlotDragEnd(event, slotId) {
			let slot = this.getSlot(slotId);
			let card = this.getCard(slot.placedCardId);

			if (this.taskType == "carddrag") {
				slot.dragged = false;
				this.$log.onSlotDragEnd(slotId, event.x, event.y, this.rawDragMouse.x, this.rawDragMouse.y);
			}
			else if (this.taskType == "arrow") {
				this.$log.onArrowDragEnd(slotId, event.x, event.y, this.rawDragMouse.x, this.rawDragMouse.y);
			}

			// if (card && this.closestSlotId != slotId) {
			if (card && !this.closestSlotId) {
				this.$log.onCardPlace(card.cardId, null, null, false);
				this.isPlaced[card.cardId] = false;
				card.clear();
				slot.clear();

				this.fillAutoslots(slot);
			}

			this.draggedSlotId = null;
			this.rawDragMouse.x = 0;
			this.rawDragMouse.y = 0;
			this.updateVerify();
		},
		onSlotDrop(event, slotId) {
			let card = this.getCard(this.draggedCardId);
			let slot = this.getSlot(slotId);

			if (this.draggedCardId) {
				this.$log.onCardPlace(card.cardId, slot.slotId, slot.placedCardId, card.cardValue == slot.cardValue);
				// if (card.cardValue == slot.cardId) {
				if (slot.placedCard) {
					this.$log.onCardPlace(slot.placedCardId, null, null, false);
					this.isPlaced[slot.placedCardId] = false;
					slot.placedCard.clear();
					this.slots.find(s => s.id == slotId).incorrect = false;
					this.updateIncorrectCount();
				}
				card.place();
				slot.place(card);
				slot.playBounce();
				this.isPlaced[this.draggedCardId] = true;

				this.fillAutoslots(slot);

				// }
				// else {
				// 	slot.playShake();
				// }
			}
			else if (this.draggedSlotId && slotId != this.draggedSlotId) {
				let oldSlot = this.getSlot(this.draggedSlotId);

				if (this.taskType == "carddrag") {
					let selectedCard = oldSlot.placedCard;
					let occupantCard = slot.placedCard;
					let selectedCardId = oldSlot.placedCardId;
					let occupantCardId = slot.placedCardId;

					this.$log.onSlotSwap(oldSlot.slotId, slot.slotId, selectedCardId, occupantCardId);
					this.$log.onCardPlace(selectedCardId, slot.slotId, occupantCardId, selectedCard.cardValue == slot.cardValue);
					if (occupantCard) {
						this.$log.onCardPlace(occupantCardId, oldSlot.slotId, selectedCardId, occupantCard.cardValue == oldSlot.cardValue);
					}

					// Swap places
					slot.place(selectedCard);
					oldSlot.place(occupantCard);
					// this.draggedSlotId = null;
					slot.playBounce();

					this.fillAutoslots(slot);
					this.fillAutoslots(oldSlot);

					this.slots.find(s => s.id == slot.slotId).incorrect = false;
					this.updateIncorrectCount();
				}
				else if (this.taskType == "arrow") {
					let arrows = [];
					if (this.$tracker.isEasy()) {
						arrows.push(this.getArrow(slotId, this.draggedSlotId));
					}
					arrows.push(this.getArrow(this.draggedSlotId, slotId));
					arrows = arrows.filter(arrow => arrow);

					this.$log.onArrowPlace(this.draggedSlotId, slotId, arrows.length > 0);

					if (arrows.length > 0) {
						for (const arrow of arrows) {
							// Found existing arrow
							if (arrow.active) {
								slot.playFlash();
								oldSlot.playFlash();
							}
							// Drawing new valid arrow
							else {
								arrow.active = true;
								this.recentArrow = arrow;

								clearTimeout(this.autoHideLabel);
								this.autoHideLabel = setTimeout(() => {
									this.recentArrow = null;
								}, 2000);

								// Reveal label slot
								for (var i = this.$refs.slots.length - 1; i >= 0; i--) {
									const slot = this.$refs.slots[i];
									if (slot.label && slot.arrow == arrow.id) {
										slot.show = true;
										this.$log.onArrowReveal(arrow.id, slot.cardValue);
									}
								}

								for (let slotData of this.slots) {
									if (slotData.arrows) {
										const i = slotData.arrows.indexOf(arrow.id);
										if (i > -1) {
											slotData.arrows.splice(i, 1);
										}
									}
								}

								let hintArrow = this.arrowHints.find(hintArrow => hintArrow.id == arrow.id);
								if (hintArrow) {
									hintArrow.found = true;
									this.arrowHintCount[hintArrow.cardValue].remaining -= 1;
									this.arrowHintCount[hintArrow.cardValue].secret = false;
									this.revealRandomHintArrow();
								}

								slot.playFlash();
								oldSlot.playFlash();
							}
						}
					}
					else {
						slot.playShake();
						oldSlot.playShake();
						this.$tracker.onTaskMistake();
					}
				}
			}

			this.updateVerify();
		},
		onSlotDown(event, slotId) {
			// let slot = this.getSlot(slotId);
			// console.log("Down", slotId);
			let { x, y } = this.graphics.setMouse(this.rawMouse, event.x, event.y);
			this.$set(this.rawMouse, 'x', x);
			this.$set(this.rawMouse, 'y', y);
			this.mouse.x = this.rawMouse.x;
			this.mouse.y = this.rawMouse.y;
			this.rawPointer.x = event.x;
			this.rawPointer.y = event.y;
			this.pointer.x = event.x;
			this.pointer.y = event.y;

			this.heldSlotId = slotId;
			this.dragStart.x = event.x;
			this.dragStart.y = event.y;

			// this.onSlotDragStart(event, slotId);
		},
		onSlotLabelEnter(slotId) {
			const slot = this.getSlot(slotId);
			for (let arrow of this.arrows) {
				if (slot && slot.arrow == arrow.id) {
					this.recentArrow = arrow;
					clearTimeout(this.autoHideLabel);
					return;
				}
			}
		},
		onSlotLabelLeave(slotId) {
			const slot = this.getSlot(slotId);
			if (slot && this.recentArrow && slot.arrow == this.recentArrow.id) {
				this.recentArrow = null;
			}
		},
		onSlotReadMore(rect, type, cardId) {
			let x = rect.left + rect.width / 2;
			let y = rect.top + rect.height / 2;
			this.$refs.modalCardInfo.open(cardId, type, x, y);
		},

		onSliderChange(sliderIndex, sliderValue) {
			// Special case for swapping the background in m3t2
			if (Array.isArray(this.background)) {
				const background = this.background[sliderIndex];
				const background_edges = this.backgroundEdges[sliderIndex];

				this.cssBack.backgroundImage = `url(${background_edges})`;
				this.cssTask.backgroundImage = `url(${background})`;
			}

			for (const group of this.groupComponents) {
				group.selectedIndex = sliderIndex;
				group.selectedValue = sliderValue;
			}

			const taskboxRefs = ["m3t2a", "m3t2b", "m3t3", "m3t4a", "m3t4b"];
			taskboxRefs.forEach(ref => {
				const obj = this.$refs[ref];
				if (obj) {
					obj.selectedIndex = sliderIndex;
					obj.selectedValue = sliderValue;
				}
			});
		},
		onAutoSlotClick(slotId, engaged) {
			const taskboxRefs = ["m3t4a", "m3t4b"];
			taskboxRefs.forEach(ref => {
				const obj = this.$refs[ref];
				if (obj) {
					obj.onAutoSlotClick(slotId, engaged);
				}
			});
		},

		fillAutoslots(slot) {
			const autos = this.autoComponents.filter(auto => auto.slotId == slot.slotId);
			for (const auto of autos) {
				auto.place(slot.placedCardValue);
			}
		},

		onPointerDown(event) {
			if (this.closestSlotId) {
				this.onSlotDown(event, this.closestSlotId);
			}
		},
		onPointerUp(event) {
			this.heldCardId = null;
			this.heldSlotId = null;

			if (this.closestSlotId) {
				this.onSlotDrop(event, this.closestSlotId);
			}

			if (this.draggedCardId) {
				this.onCardDragEnd(event, this.draggedCardId);
			}
			if (this.draggedSlotId) {
				this.onSlotDragEnd(event, this.draggedSlotId);
			}
		},
		onPointerMove(event) {
			let { x, y } = this.graphics.setMouse(this.rawMouse, event.x, event.y);
			this.$set(this.rawMouse, 'x', x);
			this.$set(this.rawMouse, 'y', y);
			this.rawPointer.x = event.x;
			this.rawPointer.y = event.y;

			if (this.heldCardId && !this.draggedCardId && this.dragStart) {
				if (this.distanceBetween(this.dragStart, event) > 20) {
					this.onCardDragStart(event, this.heldCardId);
				}
			}
			if (this.heldSlotId && !this.draggedSlotId && this.dragStart) {
				if (this.distanceBetween(this.dragStart, event) > 20) {
					this.onSlotDragStart(event, this.heldSlotId);
				}
			}

			if (this.draggedCardId) {
				this.onCardDrag(event, this.draggedCardId);
			}
			if (this.draggedSlotId) {
				this.onSlotDrag(event, this.draggedSlotId);
			}
		},

		setMouseFromEvent(event) {
			if (event.x != 0 || event.y != 0) {
				this.graphics.setMouse(this.rawDragMouse, event.x, event.y);
			}
			else {
				this.rawDragMouse.x = 0;
				this.rawDragMouse.y = 0;
			}
		},

		next() {
			this.$log.onTaskNext(this.taskId);
			// this.$emit("complete");
		},

		updateVerify() {
			this.allowVerify = this.checkVerify();

			if (this.isRunning && this.taskType == "arrow" && this.allowVerify) {
				this.onComplete();
			}
		},

		checkVerify() {
			if (!this.taskType) {
				return false;
			}
			if (this.taskType == "carddrag") {
				for (let slot of this.slotComponents) {
					if (slot.empty) {
						return false;
					}
				}
				if (this.slots.find(slotData => slotData.incorrect)) {
					return false;
				}
			}
			else if (this.taskType == "arrow") {
				for (let arrow of this.arrows) {
					if (!arrow.active /*&& !arrow.slow*/) {
						return false;
					}
				}
			}
			return true;
		},

		onVerify() {
			let success = true;

			if (this.taskType == "carddrag") {

				// Slots which you can interact with
				let usableSlots = this.slotComponents.filter(slot => !slot.locked);

				let slotIdList = usableSlots.map(slot => slot.slotId);
				let cardIdList = usableSlots.map(slot => (slot.placedCard ? slot.placedCard.cardId : null));
				let correctPairs = usableSlots.map(slot => slot.hasCorrectCard());
				let allCorrect = correctPairs.every(x => x === true);
				this.$log.onCardVerify(slotIdList, cardIdList, correctPairs, allCorrect);

				for (let slot of usableSlots) {
					if (!slot.hasCorrectCard()) {
						// this.isPlaced[slot.placedCardId] = false;
						// slot.placedCard.clear();
						// slot.clear();
						slot.playShake();
						this.slots.find(data => data.id == slot.slotId).incorrect = true;

						success = false;
						this.updateIncorrectCount();
						this.$tracker.onTaskMistake();
					}
				}
			}
			// else if (this.taskType == "arrow") {
			// 	for (let arrow of this.arrows) {
			// 		if (!arrow.active && !arrow.slow) {
			// 			return false;
			// 		}
			// 	}
			// }

			this.updateVerify();
			if (success) {
				this.onComplete();
			}
		},

		onComplete() {
			this.isRunning = false;
			this.rightAnswer = true;
			this.$log.onTaskComplete(this.taskId);
			this.$tracker.onTaskComplete(this.taskId, this.timer.elapsed);

			for (let arrow of this.arrows) {
				arrow.active = true;
				// let slot = this.getSlot(arrow.card);
				// if (slot) {
					// slot.show = true;
					// for (let card of this.cards) {
						// console.log("CARD", slot.from_card, slot.to_card);
					// }
				// }
			}

			if (this.$refs.slots) {
				for (var i = this.$refs.slots.length - 1; i >= 0; i--) {
					const slot = this.$refs.slots[i];
					slot.playFlash();

					if (slot.arrow) {
						slot.show = true;
					}
				}
			}
		},

		onResize() {
			// this.width = document.documentElement.clientWidth;
			// this.height = document.documentElement.clientHeight;
			// this.width = 0.62 * window.screen.width - 180;
			// this.height = window.screen.height - 70 - 60;
			this.graphics.onResize();
			this.$log.onWindowResize(
				document.documentElement.clientWidth,
				document.documentElement.clientHeight,
				this.graphics.getCanvasRect()
			);

			if (this.$refs.root) {
				this.top = this.$refs.root.getBoundingClientRect().top;
			}

			// const infoAreaWidth = document.documentElement.clientWidth - (0.5*document.documentElement.clientWidth + 16/9*0.13 * document.documentElement.clientHeight);
			// const infoAreaWidth = document.documentElement.clientWidth - document.documentElement.clientHeight * 0.625 * 16/9;
			const infoAreaWidth = this.$refs.infoArea.getBoundingClientRect().width;
			const infoAreaHeight = this.$refs.infoArea.getBoundingClientRect().height;
			const boxWidth = 500;
			const boxHeight = 0.95 * document.documentElement.clientHeight;
			const widthScale = Math.max(0.3, Math.min(1, infoAreaWidth / boxWidth));
			const heightScale = Math.max(0.3, Math.min(1, boxHeight / infoAreaHeight));
			const scale = Math.min(widthScale, heightScale);

			if (scale != 1) {
				this.infoBoxScaling = {
					// transform: 'scale(0.5)',
					transform: `translate(calc(50% - ${infoAreaWidth/2}px), 0%) scale(${scale})`,
					// backgroundColor: (infoAreaWidth < boxWidth) ? 'blue' : 'transparent',
					// right: 0 + 'px !important',
					// transform: `translate(calc(100% - ${infoAreaWidth}px), 0)`,
				}
			}
			else {
				this.infoBoxScaling = {};
			}
		},

		updateIncorrectCount() {
			this.incorrectCount = this.slots.filter(slot => slot.incorrect).length;
		},

		revealRandomHintArrow() {
			if (Object.keys(this.arrowHintCount).length == 0) {
				return;
			}

			let secretsLeft = Object.values(this.arrowHintCount)
				.filter(group => group.secret);
			let anyHints = Object.values(this.arrowHintCount)
				.some(group => group.remaining == group.max && !group.secret);

			if (secretsLeft.length > 0 && !anyHints) {
				let randomIndex = Math.floor(Math.random() * secretsLeft.length);
				secretsLeft[randomIndex].secret = false;
				this.$log.onArrowHint(secretsLeft[randomIndex].cardValue);
			}
		},

		update(time) {
			this.mouse.x += 0.5 * (this.rawMouse.x - this.mouse.x);
			this.mouse.y += 0.5 * (this.rawMouse.y - this.mouse.y);
			this.pointer.x += 0.5 * (this.rawPointer.x - this.pointer.x);
			this.pointer.y += 0.5 * (this.rawPointer.y - this.pointer.y);

			// if (this.clone) {
				// this.clone.style.left = this.mouse.x;
				// this.clone.style.top = this.mouse.y;
			// }

			if (this.arrows.length > 0) {

				this.prevDrawTime = this.draggedSlotId ? 0 : time;
				this.graphics.update(time);
				// this.graphics.fillCircle(this.mouse.x, this.mouse.y, 10);

				// for (var i = this.$refs.slots.length - 1; i >= 0; i--) {
				for (let arrow of this.arrows) {
					// for (var j = this.$refs.slots.length - 1; j >= 0; j--) {
					if (arrow.active) {

						// const slot1 = this.$refs.slots[i];
						const slot1 = this.getSlot(arrow.from);
						let x1 = slot1.x/100 * this.graphics.width;
						let y1 = slot1.y/100 * this.graphics.height;
						let {w, h} = slot1.getSize(this.graphics);

						// this.graphics.setStroke(2.0);
						// this.graphics.drawRoundRect(x1-w/2, y1-h/2, w, h, 10);

						// const slot2 = this.$refs.slots[j];
						const slot2 = this.getSlot(arrow.to);
						let x2 = slot2.x/100 * this.graphics.width;
						let y2 = slot2.y/100 * this.graphics.height;
						let size = slot2.getSize(this.graphics);
						let w2 = size.w;
						let h2 = size.h;

						// let arrow = this.getArrow(slot1.slotId, slot2.slotId);
						// if (arrow && arrow.active) {
							let dx1 = x1 + 0.5 * w * arrow.from_edge.x;
							let dy1 = y1 + 0.5 * h * arrow.from_edge.y;
							let dx2 = x2 + 0.5 * w2 * arrow.to_edge.x;
							let dy2 = y2 + 0.5 * h2 * arrow.to_edge.y;

							if (arrow.from_edge.x == null) dx1 = dx2;
							if (arrow.from_edge.y == null) dy1 = dy2;
							if (arrow.to_edge.x == null) dx2 = dx1;
							if (arrow.to_edge.y == null) dy2 = dy1;

							const highlight = (this.recentArrow && arrow.id == this.recentArrow.id);

							// White background
							if (!arrow.locked) {
								// this.graphics.context.strokeStyle = "#FFFFFF33";
								if (!arrow.slow) {
									this.graphics.context.strokeStyle = highlight ? "#FFFFFFEE" : "#FFFFFF28"; // White background
									// this.graphics.setStroke(8.0);
									this.graphics.setStroke(8.0 * (1.0 + 0*highlight));
								}
								else {
									this.graphics.context.strokeStyle = highlight ? "#FFFFFF77" : "#FFFFFF28"; // White background
									// this.graphics.setStroke(6.0);
									this.graphics.setStroke(6.0 * (1.0 - 0*highlight));
								}
								this.graphics.drawArrow(dx1, dy1, dx2, dy2, arrow, true);
							}

							// Black foreground
							this.graphics.context.strokeStyle = "#000000";
							if (arrow.locked) {
								this.graphics.context.strokeStyle = "#00000088";
							}
							else if (highlight) {
								this.graphics.context.strokeStyle = "#198754"; // Green
								// this.graphics.context.strokeStyle = "#FFFFFF"; // White
							}

							if (!arrow.slow) {
								this.graphics.setStroke(2.0 * (1.0+0.5*highlight) * (arrow.locked ? 0.1 : 1));
								this.graphics.drawArrow(dx1, dy1, dx2, dy2, arrow, true);
							}
							else {
								this.graphics.setStroke(1.25 * (1.0+0.5*highlight) * (arrow.locked ? 0.1 : 1), [10,10], 3*time);
								this.graphics.drawArrow(dx1, dy1, dx2, dy2, arrow, true);
							}

							// Dots
							if (!arrow.slow && !arrow.locked) {
								// Black background
								this.graphics.context.strokeStyle = "#FFFFFF";
								if (!arrow.slow)
									this.graphics.setStroke(6 * (1.0+0.5*highlight), [1,50], 20*time);
								else
									this.graphics.setStroke(6*0.66 * (1.0+0.5*highlight), [1,30], 4*time);
								// this.graphics.setStroke(2.3, [1,20], 30*time);
								// this.graphics.drawArrow(dx1, dy1, dx2, dy2, arrow, false);

								// this.graphics.context.strokeStyle = "#000000";
								this.graphics.context.strokeStyle = highlight ? "#198754" : "#000000";
								if (!arrow.slow)
									this.graphics.setStroke(3 * (1.0+0.5*highlight), [1,50], 20*time);
								else
									this.graphics.setStroke(3*0.66 * (1.0+0.5*highlight), [1,30], 4*time);
								this.graphics.drawArrow(dx1, dy1, dx2, dy2, arrow, false);
							}
						// }
					}
				}

				if (this.taskType == "arrow" && this.draggedSlotId) {
					// if (this.rawMouse.x != 0 || this.rawMouse.y != 0) {
						// this.mouse.x += 0.75 * (this.rawMouse.x - this.mouse.x);
						// this.mouse.y += 0.75 * (this.rawMouse.y - this.mouse.y);
						// this.graphics.circle(this.dragMouse.x, this.dragMouse.y, 5).fill();
					// }

					const slot = this.getSlot(this.draggedSlotId);
					const {w, h} = slot.getSize(this.graphics);

					const x2 = this.mouse.x;
					const y2 = this.mouse.y;
					const cx = slot.x/100 * this.graphics.width;
					const cy = slot.y/100 * this.graphics.height;
					let x1 = Math.max(Math.min(x2, cx+w/2), cx-w/2);
					let y1 = Math.max(Math.min(y2, cy+h/2), cy-h/2);

					const dx = Math.abs(x2 - x1);
					const dy = Math.abs(y2 - y1);
					const props = {
						from_edge: {
							x: (dx <= dy ? 0 : (x2 > x1 ? 1 : -1)),
							y: (dx > dy ? 0 : (y2 > y1 ? 1 : -1)),
						},
						to_edge: {
							x: 0,
							y: 0,
						}
					};

					if (dx != 0 || dy != 0) {


						if (props.from_edge.x != 0) {
							// y1 = Math.max(Math.min(y2, cy+h/4), cy-h/4);
							y1 = cy;
						}
						else {
							// x1 = Math.max(Math.min(x2, cx+w/4), cx-w/4);
							x1 = cx;
						}

						// if (x1 == x2) {
						// 	props.to_edge.x = 0;
						// 	props.to_edge.y = -props.from_edge.y;
						// }
						// else if (y1 == y2) {
						// 	props.to_edge.x = -props.from_edge.x;
						// 	props.to_edge.y = 0;
						// }
						// else {
						// 	props.to_edge.x = -props.from_edge.y;
						// 	props.to_edge.y = -props.from_edge.x;
						// }

						this.graphics.context.strokeStyle = "#FFFFFF33";
						this.graphics.setStroke(8);
						// this.graphics.drawArrow(x1, y1, x2, y2, props, true);
						this.graphics.drawLine(x1, y1, x2, y2);
						this.graphics.drawArrowhead(x2, y2, x2-x1, y2-y1);
						if (this.$tracker.isEasy())
							this.graphics.drawArrowhead(x1, y1, x1-x2, y1-y2);

						// this.graphics.context.strokeStyle = this.closestSlotId ? "#ffc107" : "#000000";
						this.graphics.context.strokeStyle = "#000000";
						this.graphics.setStroke(3, [1, 8]);
						// this.graphics.drawArrow(x1, y1, x2, y2, props, true);
						this.graphics.drawLine(x1, y1, x2, y2);
						this.graphics.drawArrowhead(x2, y2, x2-x1, y2-y1);
						if (this.$tracker.isEasy())
							this.graphics.drawArrowhead(x1, y1, x1-x2, y1-y2);
					}
				}
			}
		},
	},

	computed: {
		graphicsWidth() {
			return this.graphics ? this.graphics.width : 0;
		},

		slotComponents() {
			let components = [];
			for (let i = 0; i < this.slots.length; i++) {
				// const ref = this.$refs['slot'+this.slots[i].id];
				// if (ref) {
					// components.push(ref[0]);
				// }
				let slot = this.getSlot(this.slots[i].id);
				if (slot) {
					components.push(slot);
				}
			}
			return components;
		},

		autoComponents() {
			let components = [];
			for (let i = 0; i < this.autoslots.length; i++) {
				let auto = this.getAutoslot(this.autoslots[i].id);
				if (auto) {
					components.push(auto);
				}
			}
			return components;
		},

		groupComponents() {
			let components = [];
			for (let i = 0; i < this.groups.length; i++) {
				let group = this.getGroup(this.groups[i].id);
				if (group) {
					components.push(group);
				}
			}
			return components;
		},

		sliderComponents() {
			let components = [];
			for (let i = 0; i < this.sliders.length; i++) {
				let slider = this.getSlider(this.sliders[i].id);
				if (slider) {
					components.push(slider);
				}
			}
			return components;
		},

		closestSlotId() {
			const mx = this.rawMouse.x;
			const my = this.rawMouse.y;

			let bestId = null;
			let record = 1;

			if (this.$refs.slots) {
				for (var i = this.$refs.slots.length - 1; i >= 0; i--) {
					const slot = this.$refs.slots[i];
					if (slot.show && !slot.label) {
						let {w, h} = slot.getSize(this.graphics);
						let x = slot.x/100 * this.graphics.width + (1 - 2*slot.anchor.x) * w/2;
						let y = slot.y/100 * this.graphics.height + (1 - 2*slot.anchor.y) * h/2;

						let dist = Math.pow((x - mx)/(0.5*(w+100)), 4) + Math.pow((y - my)/(0.5*(h+100)), 4);
						// let dist = (x - mx)*(x - mx) + (y - my)*(y - my);
						let allowCard = this.taskType == "carddrag" && !slot.locked;
						let allowArrow = this.taskType == "arrow" && (slot.slotId != this.draggedSlotId || slot.arrowCount > 0);

						if (dist < record && (allowCard || allowArrow)) {
							bestId = slot.slotId;
							record = dist;
						}
					}
				}
			}
			return bestId;
		},

		draggedCardValue() {
			if (this.taskType == "carddrag") {
				if (this.draggedCardId)
					return this.getCard(this.draggedCardId).cardValue;
				if (this.draggedSlotId)
					return this.getSlot(this.draggedSlotId).placedCardValue;
			}
		},

		// Check if explanation text exists
		hasExplanation() {
			return this.$te(this.taskId + "_explanation_description");
		},

		cardCount() {
			return this.cards.filter(card => this.isPlaced[card.id]).length - this.incorrectCount - (this.draggedSlotId ? 1 : 0);
		},
		maxCardCount() {
			return this.slots.filter(slot => !slot.locked).length;
		},
		arrowCount() {
			return this.arrows.filter(arrow => arrow.active && !arrow.id.startsWith("arrow_legend")).length;
		},
		maxArrowCount() {
			// return this.arrows.filter(arrow => arrow.active).length;
			return this.arrows.filter(arrow => !arrow.id.startsWith("arrow_legend")).length;
		},
		anyDragged() {
			return this.draggedSlotId || this.draggedCardId;
		},
	},
	watch: {
		graphicsWidth() {
			let widthRatio = this.graphics.width / this.graphics.rect.width;
			let heightRatio = this.graphics.height / this.graphics.rect.height;
			for (const slot of this.slotComponents) {
				slot.widthRatio = widthRatio;
				slot.heightRatio = heightRatio;
			}
			for (const auto of this.autoComponents) {
				auto.widthRatio = widthRatio;
				auto.heightRatio = heightRatio;
			}
			for (const group of this.groupComponents) {
				group.widthRatio = widthRatio;
				group.heightRatio = heightRatio;
			}
			for (const slider of this.sliderComponents) {
				slider.widthRatio = widthRatio;
				slider.heightRatio = heightRatio;
			}
		},
		anyDragged() {
			if (this.anyDragged) {
				this.timer.wake();
			}
			else {
				this.timer.sleep();
			}
		}
	},
};
</script>



<style scoped>
	#background {
		flex-grow: 1;
		display: flex;
		/*overflow: hidden;*/

		/*background-color: black;*/
		background-image: unset;
		background-repeat: no-repeat;
		background-position: center;
		/*background-size: cover;*/
		background-size: 100% 100%;
		overflow-y: hidden;
		overflow-x: hidden;
	}

	#task {
		flex-grow: 1;
		display: flex;
		position: relative;

		background-image: unset;
		background-repeat: no-repeat;
		background-position: center;
		/*background-size: cover;*/
		background-size: auto 100%;
		backdrop-filter: blur(4px);
	}

	#infoArea {
		/*background: rgba(0, 255, 0, 0.5);*/
		/*width: calc(33.75vw - 0vh*16/9);*/
		/*width: calc(100vw - 0.625*100vh*16/9);*/
		width: calc(50vw - 100vh*16/9*0.13);
		align-self: center;
		position: absolute;
		/*right: max(calc(50vw + 100vh*16/9*0.13), calc(100vw - 0.625*100vh*16/9));*/
		/*transform: translate(-50%, 0);*/
		z-index: 1;
	}

	#dropArea {
		position: absolute;
		width: 100%;
		height: 100%;
		left: calc(50vw - 100vh*16/9*0.13);
		top: 0%;
		/*flex-grow: 1;*/
		width: calc(0.625*100vh*16/9);
	}

	#canvas {
		position: absolute;
		width: 100%;
		height: 100%;
		pointer-events: none;
	}


	#infoBox {
		min-width: 500px;
		max-width: 650px;
		background-color: rgba(255, 255, 255, 0.75);
		backdrop-filter: blur(5px);
		border-radius: 0.4em;
		margin: 1vmin;
		padding: 0.1em;
		float: right;
		/*position: absolute;*/
		/*right: 0;*/
		/*transform: translate(0, -50%);*/
		/*max-height: 85vh;*/
		overflow-y: auto;
		/*margin: auto auto;*/
	}

	#infoIntroduction,
	#infoInstruction,
	#infoCards,
	#infoArrows {
		background-color: rgba(255, 255, 255, 1.0);
		/*backdrop-filter: blur(10px);*/
		border: 1px solid #CCC;
		border-radius: 0.4em;
		margin: 0.5em;
		padding: 1em;
	}

	#infoIntroduction {}

	#infoInstruction {}

	#infoCards {
		display: flex;
		flex-wrap: wrap;
		padding: 0.5em;
		justify-content: center;
		overflow-y: auto;
	}

	#infoArrows {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 12px 8px;
	}

	.card-text {
		/*padding: 0.25em;*/
		line-height: 1.7em;
	}

	.arrow-hint {
		position: relative;
		overflow-x: hidden;
		/* overflow-y: auto; */
	}
	.arrow-hint-label {
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.arrow-hint-count-wrapper {
		position: absolute;
		left: 8px;
		top: -0.5em;
	}

	.arrow-hint-count {
		position: fixed;
		transform: translateX(-50%);
		font-size: 0.6em;
		text-align: center;
	}

	.hint-bounce {
		animation: Bounce .6s ease-out;
	}
	@keyframes Bounce {
		0%	{ transform: scale(1.10); }
		25%	{ transform: scale(0.95); }
		50%	{ transform: scale(1.02); }
		75%	{ transform: scale(0.99); }
		100%{ transform: scale(1.00); }
	}


	.loading-icon {
		position: fixed;
		text-align: center;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}


	@media (max-aspect-ratio: 159/90) {
		#task {
			background-position: calc(100% + 23vh);
		}

		#infoArea {
			/*background: rgba(255, 0, 0, 0.5);*/
			width: calc(100vw - 0.625*100vh*16/9);
			/*right: calc(50vw + 100vh*16/9*0.13);*/
			/*right: calc(100vw - 0.625*100vh*16/9);*/
		}

		#infoBox {
			margin: 0 !important;
		}

		#dropArea {
			left: unset;
			right: 0 !important;
		}
	}
</style>
